/**
 * Pure utility functions for parsing and validating pricing rule imports.
 *
 * Extracted from PricingRulesImportDialog for testability.
 * These functions have no React dependencies — they operate on plain data.
 */

import Papa from 'papaparse';

import type { FacetGroup } from '@src/facets';
import type { ConditionOption } from '@src/conditions';
import { SOURCE_COLUMNS } from './types';
import type { PricingRuleInput, PricingType } from './types';

export interface ParsedRule extends Partial<PricingRuleInput> {
  _rowIndex: number;
  _hasError?: boolean;
  _errorMessage?: string;
}

export const HEADER_MAP: Record<string, keyof PricingRuleInput> = {
  source_column: 'sourceColumn',
  department: 'department',
  category: 'category',
  merchant_category: 'merchantCategory',
  upc: 'upc',
  primary_tag: 'tag',
  primary_name: 'name',
  secondary_tag: 'secondaryTag',
  secondary_name: 'secondaryName',
  tertiary_tag: 'tertiaryTag',
  tertiary_name: 'tertiaryName',
  discount: 'discount',
  condition: 'condition',
};

export function parseTsvFile(content: string, pricingType: PricingType): { rules: ParsedRule[], errors: string[] } {
  const result = Papa.parse<Record<string, string>>(content, {
    delimiter: '\t',
    header: true,
    skipEmptyLines: true,
    transformHeader: h => h.toLowerCase().trim(),
  });

  if (result.data.length === 0) {
    return { rules: [], errors: ['File must have a header row and at least one data row'] };
  }

  const rules: ParsedRule[] = [];
  const errors: string[] = [];

  result.data.forEach((row, index) => {
    const rowIndex = index + 2; // +2 because: 0-indexed + header row
    const rule: ParsedRule = { _rowIndex: rowIndex };

    // Map CSV columns to rule fields
    Object.entries(row).forEach(([header, value]) => {
      const fieldName = HEADER_MAP[header];
      if (fieldName && value !== undefined && value.trim() !== '') {
        (rule as unknown as Record<string, unknown>)[fieldName] = value.trim();
      }
    });

    // Parse discount as number
    if (rule.discount !== undefined) {
      const discount = parseFloat(String(rule.discount));
      if (Number.isNaN(discount)) {
        rule._hasError = true;
        rule._errorMessage = `Invalid discount value: ${rule.discount}`;
        errors.push(`Row ${rowIndex}: Invalid discount value`);
      } else {
        rule.discount = discount;
      }
    } else {
      rule._hasError = true;
      rule._errorMessage = 'Missing discount value';
      errors.push(`Row ${rowIndex}: Missing discount value`);
    }

    // Default source column for yerdle_price only
    if (pricingType !== 'condition' && !rule.sourceColumn) {
      rule.sourceColumn = 'price';
    }

    rules.push(rule);
  });

  // Add any parsing errors from papaparse
  result.errors.forEach((err) => {
    const rowNum = err.row !== undefined ? err.row + 2 : '?';
    errors.push(`Row ${rowNum}: ${err.message}`);
  });

  return { rules, errors };
}

export function validateRules(
  rules: ParsedRule[],
  pricingType: PricingType,
  facetGroups: FacetGroup[],
  conditions: ConditionOption[],
): { rules: ParsedRule[], errors: string[] } {
  const validatedRules: ParsedRule[] = [];
  const errors: string[] = [];

  if (pricingType === 'condition') {
    const validConditionCodes = conditions.map(c => c.code);
    const validDepartments = facetGroups.find(fg => fg.tag === 'department')?.values.map(v => v.name) || [];
    const validCategories = facetGroups.find(fg => fg.tag === 'category')?.values.map(v => v.name) || [];
    const validTags = facetGroups.map(fg => fg.tag);
    const nameMap: Record<string, string[]> = {};
    facetGroups.forEach((fg) => { nameMap[fg.tag] = fg.values.map(v => v.name); });

    rules.forEach((rule) => {
      if (rule._hasError) {
        validatedRules.push(rule);
        return;
      }

      const ruleErrors: string[] = [];

      // Validate condition code (optional — empty means catch-all)
      // validConditionCodes is guaranteed non-empty by the conditionsLoading guard in handleFileChange
      if (rule.condition && !validConditionCodes.includes(rule.condition)) {
        ruleErrors.push(`Invalid condition: ${rule.condition}`);
      }

      // Validate facet-scoped fields (same logic as yerdle_price branch)
      if (rule.department && validDepartments.length > 0 && !validDepartments.includes(rule.department)) {
        ruleErrors.push(`Invalid department: ${rule.department}`);
      }
      if (rule.category && validCategories.length > 0 && !validCategories.includes(rule.category)) {
        ruleErrors.push(`Invalid category: ${rule.category}`);
      }
      if (rule.tag && validTags.length > 0 && !validTags.includes(rule.tag)) {
        ruleErrors.push(`Invalid tag: ${rule.tag}`);
      }
      if (rule.name && rule.tag) {
        const validNames = nameMap[rule.tag] || [];
        if (validNames.length > 0 && !validNames.includes(rule.name)) {
          ruleErrors.push(`Invalid name "${rule.name}" for tag "${rule.tag}"`);
        }
      }

      const validatedRule = { ...rule };
      if (ruleErrors.length > 0) {
        validatedRule._hasError = true;
        validatedRule._errorMessage = ruleErrors.join('; ');
        errors.push(`Row ${rule._rowIndex}: ${ruleErrors.join('; ')}`);
      }
      validatedRules.push(validatedRule);
    });

    return { rules: validatedRules, errors };
  }

  const validDepartments = facetGroups.find(fg => fg.tag === 'department')?.values.map(v => v.name) || [];
  const validCategories = facetGroups.find(fg => fg.tag === 'category')?.values.map(v => v.name) || [];
  const validTags = facetGroups.map(fg => fg.tag);

  // Build name map: tag -> valid names
  const nameMap: Record<string, string[]> = {};
  facetGroups.forEach((fg) => {
    nameMap[fg.tag] = fg.values.map(v => v.name);
  });

  rules.forEach((rule) => {
    const ruleErrors: string[] = [];

    // Skip if already has error
    if (rule._hasError) {
      validatedRules.push(rule);
      return;
    }

    // Validate department
    if (rule.department && validDepartments.length > 0 && !validDepartments.includes(rule.department)) {
      ruleErrors.push(`Invalid department: ${rule.department}`);
    }

    // Validate category
    if (rule.category && validCategories.length > 0 && !validCategories.includes(rule.category)) {
      ruleErrors.push(`Invalid category: ${rule.category}`);
    }

    // Validate tag
    if (rule.tag && validTags.length > 0 && !validTags.includes(rule.tag)) {
      ruleErrors.push(`Invalid tag: ${rule.tag}`);
    }

    // Validate name matches tag
    if (rule.name && rule.tag) {
      const validNames = nameMap[rule.tag] || [];
      if (validNames.length > 0 && !validNames.includes(rule.name)) {
        ruleErrors.push(`Invalid name "${rule.name}" for tag "${rule.tag}"`);
      }
    }

    // Validate secondary tag
    if (rule.secondaryTag && validTags.length > 0 && !validTags.includes(rule.secondaryTag)) {
      ruleErrors.push(`Invalid secondaryTag: ${rule.secondaryTag}`);
    }

    // Validate secondary name matches secondary tag
    if (rule.secondaryName && rule.secondaryTag) {
      const validNames = nameMap[rule.secondaryTag] || [];
      if (validNames.length > 0 && !validNames.includes(rule.secondaryName)) {
        ruleErrors.push(`Invalid secondaryName "${rule.secondaryName}" for secondaryTag "${rule.secondaryTag}"`);
      }
    }

    // Validate tertiary tag
    if (rule.tertiaryTag && validTags.length > 0 && !validTags.includes(rule.tertiaryTag)) {
      ruleErrors.push(`Invalid tertiaryTag: ${rule.tertiaryTag}`);
    }

    // Validate tertiary name matches tertiary tag
    if (rule.tertiaryName && rule.tertiaryTag) {
      const validNames = nameMap[rule.tertiaryTag] || [];
      if (validNames.length > 0 && !validNames.includes(rule.tertiaryName)) {
        ruleErrors.push(`Invalid tertiaryName "${rule.tertiaryName}" for tertiaryTag "${rule.tertiaryTag}"`);
      }
    }

    // Validate source column
    if (rule.sourceColumn && !(SOURCE_COLUMNS as readonly string[]).includes(rule.sourceColumn)) {
      ruleErrors.push(`Invalid sourceColumn: ${rule.sourceColumn}`);
    }

    const validatedRule = { ...rule };
    if (ruleErrors.length > 0) {
      validatedRule._hasError = true;
      validatedRule._errorMessage = ruleErrors.join('; ');
      errors.push(`Row ${rule._rowIndex}: ${ruleErrors.join('; ')}`);
    }
    validatedRules.push(validatedRule);
  });

  return { rules: validatedRules, errors };
}
